import { isEmpty, isNil } from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useReducer, useState } from 'react';
import { InputComponent } from '@shared/scenes/workflows/InputComponent';
import { useWorkflowTemplate } from '@shared/scenes/workflows/hooks';
import { isGroupableComponent, isInputComponent } from '@shared/scenes/workflows/utils';
import { useDebouncedState, usePersistentRef, useStableValue } from '@shared/util/hooks';
import {
  ControlsActions,
  ControlsLoadingState,
  ControlsState,
  FilterOption,
  SearchInputState,
  SortOption,
} from '../types';
import { useBulkUpdateState } from './BulkUpdateContext';
import {
  ControlsActionsProvider,
  ControlsLoadingProvider,
  ControlsStateProvider,
  SearchInputProvider,
} from './ControlsContext';
import { PreserveCollectionCriteria } from './PreserveCollectionCriteria';
import { useViewTemplateActions, useViewTemplateState } from './ViewTemplateContext';
import { useCollectionCriteriaQueryParamOverride } from './collectionViewQueryParamOverrides';
import { getRelatedEntityFilterComponentIds } from './getRelatedEntityFilterComponentIds';
import { hasUnresolvedRelatedFilters } from './hasUnresolvedRelatedFilters';
import { isCollectionCriteriaEqual } from './isCollectionCriteriaEqual';
import { mapCollectionCriteriaToState } from './mapCollectionCriteriaToState';
import { mapStateToCollectionCriteria } from './mapStateToCollectionCriteria';
import { reducer } from './reducer';
import { toFilterOptions, toGroupOptions, toSortOptions } from './toFilterOptions';
import { useRelatedFilterOptions } from './useRelatedFilterOptions';

interface ProviderProps {
  children: ReactNode;
  workflowTemplateId: string;
}

const SEARCH_DEBOUNCE_MS = 500;

export function ControlsProvider({ children, workflowTemplateId }: ProviderProps) {
  const { setViewTemplate } = useViewTemplateActions();
  const { viewTemplate } = useViewTemplateState();
  const [savedCriteria, setSavedCriteria] = useStableValue(viewTemplate.collectionCriteria);
  const components = useInputComponents(workflowTemplateId);
  const { showBulkFilterUpdate } = useBulkUpdateState();
  const [showGroupMenu, setShowGroupMenu] = useState(true);
  // Read once at mount and never re-read afterward (see
  // `useCollectionCriteriaQueryParamOverride`), so this stays stable for the rest of the mount -
  // no need to latch it against later becoming null.
  const queryParamCriteria = useCollectionCriteriaQueryParamOverride();

  const availableFilters: FilterOption[] = useMemo(() => {
    return toFilterOptions(components);
  }, [components]);

  const availableSorts: SortOption[] = useMemo(() => {
    return toSortOptions(components);
  }, [components]);

  const availableGroups: FilterOption[] = useMemo(() => {
    return toGroupOptions(components.filter(isGroupableComponent));
  }, [components]);

  const {
    relatedFilters: availableRelatedFilters,
    relatedGroups: availableRelatedGroups,
    requestRelatedFilterOptions,
  } = useRelatedFilterOptions(components);

  // only read at mount - later changes to the saved criteria (including nested options
  // arriving) come back through `revertCollectionCriteria` below, so this is initialized
  // lazily rather than mapped again on every render
  const [state, dispatch] = useReducer(reducer, null, () =>
    mapCollectionCriteriaToState(
      savedCriteria ?? null,
      availableFilters,
      availableSorts,
      availableGroups,
      availableRelatedFilters,
      availableRelatedGroups,
    ),
  );

  // One value at two speeds: `searchInput` is what the search field renders on every
  // keystroke, `search` is what the query runs on. The field holds no copy of its own.
  const [debouncedSearch, setSearch, searchInput] = useDebouncedState<string | null>(
    null,
    SEARCH_DEBOUNCE_MS,
  );
  const search = getSearchQuery(searchInput, debouncedSearch);
  const [isModified, setIsModified] = useState(false);

  // ids of the card queries currently in flight, tracked as a set because a
  // collection runs several at once and each finishes on its own schedule
  const [loadingQueryIds, setLoadingQueryIds] = useState<ReadonlySet<string>>(() => new Set());

  const setLoading: ControlsActions['setLoading'] = useCallback((id, isLoading) => {
    setLoadingQueryIds(prev => {
      if (prev.has(id) === isLoading) {
        return prev;
      }
      const next = new Set(prev);
      if (isLoading) {
        next.add(id);
      } else {
        next.delete(id);
      }
      return next;
    });
  }, []);

  // the query state (`filters`/`sorts`/`groups`) and the menu rows (`selected*`) are all
  // owned by the reducer so they can't drift apart
  const { filters, selectedFilters, sorts, selectedSorts, groups, selectedGroups } = state;

  const dispatchFilterAction: ControlsActions['dispatchFilterAction'] = useCallback(
    (filter, operation, selection, negate) => {
      dispatch({ type: 'filter', operation, payload: { filter, selection, negate } });
    },
    [dispatch],
  );

  const dispatchSortAction: ControlsActions['dispatchSortAction'] = useCallback(
    (sort, operation) => {
      dispatch({ type: 'sort', operation, payload: sort });
    },
    [dispatch],
  );

  const dispatchGroupAction: ControlsActions['dispatchGroupAction'] = useCallback(
    (filter, operation, index, selection) => {
      dispatch({ type: 'group', operation, index, payload: { filter, selection } });
    },
    [dispatch],
  );

  const resetState = useCallback(
    (nextState: ControlsState) => {
      dispatch({ type: 'reset', payload: nextState });
    },
    [dispatch],
  );

  const revertCollectionCriteria: ControlsActions['revertCollectionCriteria'] = useCallback(() => {
    // revert state to saved version in viewTemplate
    const nextState = mapCollectionCriteriaToState(
      savedCriteria ?? null,
      availableFilters,
      availableSorts,
      availableGroups,
      availableRelatedFilters,
      availableRelatedGroups,
    );
    resetState(nextState);
  }, [
    savedCriteria,
    availableFilters,
    availableGroups,
    availableSorts,
    availableRelatedFilters,
    availableRelatedGroups,
    resetState,
  ]);

  useEffect(() => {
    // Cross-workflow filters in saved criteria (or in a shared link) can't be decoded until the
    // related workflow templates they point at have loaded, so ask for them here. Once they
    // arrive, `hasUnresolvedSavedRelatedFilters` clears and the sync effect below re-maps the
    // criteria with the nested filters included.
    requestRelatedFilterOptions([
      ...getRelatedEntityFilterComponentIds(savedCriteria),
      ...getRelatedEntityFilterComponentIds(queryParamCriteria),
    ]);
  }, [savedCriteria, queryParamCriteria, requestRelatedFilterOptions]);

  useEffect(() => {
    // update local savedCriteria when viewTemplate collection criteria changes
    setSavedCriteria(prev => {
      if (isCollectionCriteriaEqual(prev ?? null, viewTemplate.collectionCriteria ?? null)) {
        return prev;
      } else {
        return viewTemplate.collectionCriteria ?? null;
      }
    });
  }, [viewTemplate, setSavedCriteria]);

  // `revertCollectionCriteria` closes over the related options, so it takes a new identity every
  // time demand is registered for another related card - which is what expanding one in a filter or
  // group menu does. Driving the sync below off it directly would therefore re-apply the saved
  // criteria on every expand, discarding in-progress edits. Read it through a ref instead, and
  // re-sync on the one related-options change that matters: the nested options `savedCriteria`
  // itself references arriving.
  const revertCollectionCriteriaRef = usePersistentRef(revertCollectionCriteria);
  const hasUnresolvedSavedRelatedFilters = hasUnresolvedRelatedFilters(
    savedCriteria,
    availableRelatedFilters,
    availableRelatedGroups,
  );

  useEffect(() => {
    // reset state when savedCriteria changes
    if (!isNil(queryParamCriteria)) {
      // A collectionCriteria query param override takes precedence and is applied by
      // PreserveCollectionCriteria instead; don't let this sync clobber it.
      return;
    }
    revertCollectionCriteriaRef.current();
  }, [
    savedCriteria,
    availableFilters,
    availableSorts,
    availableGroups,
    hasUnresolvedSavedRelatedFilters,
    queryParamCriteria,
    revertCollectionCriteriaRef,
  ]);

  const updateViewTemplateCollectionCriteria: ControlsActions['updateViewTemplateCollectionCriteria'] =
    useCallback(() => {
      const updatedCriteria = mapStateToCollectionCriteria({ filters, sorts, groups });
      setViewTemplate(prev => ({ ...prev, collectionCriteria: updatedCriteria }));
    }, [filters, sorts, groups, setViewTemplate]);

  const controlsStateValue: ControlsState = useMemo(
    () => ({
      filters,
      groups: showBulkFilterUpdate ? [] : groups,
      availableFilters,
      selectedFilters,
      sorts,
      availableSorts,
      selectedSorts,
      availableGroups,
      selectedGroups,
      availableRelatedFilters,
      availableRelatedGroups,
      search,
      isModified,
      showGroupMenu,
    }),
    [
      availableFilters,
      availableGroups,
      availableSorts,
      availableRelatedFilters,
      availableRelatedGroups,
      search,
      selectedFilters,
      selectedGroups,
      selectedSorts,
      filters,
      sorts,
      groups,
      isModified,
      showBulkFilterUpdate,
      showGroupMenu,
    ],
  );

  const searchInputValue: SearchInputState = useMemo(() => ({ searchInput }), [searchInput]);

  // memoized on the boolean, not the set, so adding a second in-flight query
  // doesn't publish a new value
  const isLoading = loadingQueryIds.size > 0;
  const controlsLoadingValue: ControlsLoadingState = useMemo(() => ({ isLoading }), [isLoading]);

  const controlsActionsValue: ControlsActions = useMemo(
    () => ({
      dispatchFilterAction,
      dispatchSortAction,
      dispatchGroupAction,
      requestRelatedFilterOptions,
      setSearch,
      setLoading,
      revertCollectionCriteria,
      updateViewTemplateCollectionCriteria,
      setShowGroupMenu,
    }),
    [
      requestRelatedFilterOptions,
      dispatchFilterAction,
      dispatchSortAction,
      dispatchGroupAction,
      revertCollectionCriteria,
      setSearch,
      setLoading,
      updateViewTemplateCollectionCriteria,
      setShowGroupMenu,
    ],
  );

  useEffect(() => {
    // When switching views, reset showGroupMenu to true. Calendar views will
    // set this value as needed for themselves.
    if (viewTemplate.subtype !== 'CALENDAR') {
      setShowGroupMenu(true);
    }
  }, [viewTemplate.subtype, setShowGroupMenu]);

  return (
    <ControlsActionsProvider value={controlsActionsValue}>
      <ControlsStateProvider value={controlsStateValue}>
        <SearchInputProvider value={searchInputValue}>
          <ControlsLoadingProvider value={controlsLoadingValue}>
            <PreserveCollectionCriteria
              setIsModified={setIsModified}
              resetState={resetState}
              queryParamCriteria={queryParamCriteria}
            />
            {children}
          </ControlsLoadingProvider>
        </SearchInputProvider>
      </ControlsStateProvider>
    </ControlsActionsProvider>
  );
}

function getSearchQuery(searchInput: string | null, debouncedSearch: string | null) {
  // an emptied field takes effect straight away - there's nothing to wait for, and the
  // results (plus anything keyed off an active search, like the flattened tree note)
  // should come back at once rather than a beat later
  if (isEmpty(searchInput)) {
    return null;
  }
  const query = debouncedSearch?.trim();
  return isNil(query) || isEmpty(query) ? null : query;
}

function useInputComponents(workflowTemplateId: string): InputComponent[] {
  const { workflowTemplate } = useWorkflowTemplate(workflowTemplateId);
  return useMemo(() => {
    const components = workflowTemplate?.components ?? [];
    return components.filter(isInputComponent);
  }, [workflowTemplate]);
}
